import axios from "axios";
import type { AppThunk } from "@Store/index";
import { getMetaSuccess } from "@App/appReducer";
import getBlogCollectionUrl from "@Pages/Blog/utils/getBlogCollectionUrl";
import {
  BLOG_ARTICLES_COLLECTION,
  FEATURED_ARTICLES_COLLECTION,
  HERO_POST_COUNT,
  RECENT_POST_COUNT,
  REGION_POST_COUNT,
} from "@Pages/Blog/BlogHomeRevamp/settings";
import {
  PUBLISHED_POSTS_QUERY,
  buildBlogArticlesListQuery,
  toBlogCardPost,
  type BlogCardPostResponse,
} from "@Pages/Blog/BlogHomeRevamp/utils/blogCardPost";
import getBlogHomeSchema from "@Pages/Blog/BlogHomeRevamp/utils/blogHomeSchema";
import { fetchBlogHomeWriters } from "@Pages/Blog/BlogHomeRevamp/utils/blogHomeCms";
import { fetchLiveBlogRegions } from "@Pages/Blog/BlogHomeRevamp/utils/fetchLiveBlogRegions";
import {
  updateBlogHomeError,
  updateBlogHomeReadyStatus,
  updateBlogHomeRevampData,
} from "./blogHomeReducer";

type DocsResponse = { docs: BlogCardPostResponse[] };

const EMPTY_DOCS: DocsResponse = { docs: [] };

export { PUBLISHED_POSTS_QUERY } from "@Pages/Blog/BlogHomeRevamp/utils/blogCardPost";

const META_TITLE = "Amber Blog - Student Guide, Accommodation Articles, and More";
const META_DESCRIPTION =
  "Find the latest student guides on accommodation, cities, and more with amber's curated lists of topics to help you find your next adventure at a home away from home.";

const getBlogHomeRevampData =
  ({ staticContext }: { staticContext?: any } = {}): AppThunk =>
  async (dispatch) => {
    const isServerRender = Boolean(staticContext);

    try {
      dispatch(updateBlogHomeReadyStatus("request"));

      const postsUrl = getBlogCollectionUrl(BLOG_ARTICLES_COLLECTION);

      const writersRequest = fetchBlogHomeWriters();
      const liveRegionsRequest = fetchLiveBlogRegions();

      const [featuredResponse, latestResponse, homeWriters, liveRegions] = await Promise.all([
        axios
          .get<{ data: BlogCardPostResponse[] }>(getBlogCollectionUrl(FEATURED_ARTICLES_COLLECTION))
          .catch(() => ({ data: { data: [] as BlogCardPostResponse[] } })),
        axios
          .get<DocsResponse>(`${postsUrl}?${PUBLISHED_POSTS_QUERY}&limit=${RECENT_POST_COUNT}`)
          .catch(() => ({ data: EMPTY_DOCS })),
        writersRequest,
        liveRegionsRequest,
      ]);

      const regionResponses = await Promise.all(
        liveRegions.homeRegionOrder.map((countrySlug) =>
          axios
            .get<DocsResponse>(
              `${postsUrl}?${buildBlogArticlesListQuery(
                { countrySlug },
                { limit: REGION_POST_COUNT },
              )}`,
            )
            .catch(() => ({ data: EMPTY_DOCS })),
        ),
      );

      const heroPosts = (featuredResponse.data?.data || [])
        .slice(0, HERO_POST_COUNT)
        .map(toBlogCardPost);
      const latestPosts = (latestResponse.data?.docs || []).map(toBlogCardPost);
      const regionPosts = liveRegions.homeRegionOrder.reduce<
        Record<string, ReturnType<typeof toBlogCardPost>[]>
      >((acc, countrySlug, index) => {
        acc[countrySlug] = (regionResponses[index]?.data?.docs || []).map(toBlogCardPost);
        return acc;
      }, {});

      if (heroPosts.length === 0 && latestPosts.length === 0) {
        dispatch(updateBlogHomeReadyStatus("failure"));
        if (isServerRender) {
          staticContext.statusCode = 404;
        }
        return;
      }

      const meta: any = {
        title: META_TITLE,
        description: META_DESCRIPTION,
        keywords: ["student accommodation", "study abroad", "student housing", "student blog"],
        canonical_url_suffix: "/blog",
        schemaData: getBlogHomeSchema(
          [...heroPosts, ...latestPosts, ...Object.values(regionPosts).flat()],
          { title: META_TITLE, description: META_DESCRIPTION },
        ),
        excludeSiteSchema: true,
      };
      dispatch(getMetaSuccess(meta));
      dispatch(
        updateBlogHomeRevampData({
          heroPosts,
          latestPosts,
          regionPosts,
          writers: homeWriters.writers,
          writersSubtitle: homeWriters.subtitle,
          regionOrder: liveRegions.homeRegionOrder,
          regionDescriptions: liveRegions.regionDescriptions,
          liveRegionSlugs: liveRegions.liveRegionSlugs,
          usesHomeRegionFallback: liveRegions.usesHomeRegionFallback,
          regionsByCountrySlug: liveRegions.regionsByCountrySlug,
        }),
      );
      dispatch(updateBlogHomeReadyStatus("success"));
    } catch (error) {
      console.error("Error fetching blog home revamp data:", error);
      dispatch(updateBlogHomeError(error));
      dispatch(updateBlogHomeReadyStatus("failure"));
      if (isServerRender) {
        staticContext.statusCode = 500;
      }
    }
  };

export default getBlogHomeRevampData;
